import React, { useEffect, useRef } from 'react';
import { styled } from 'linaria/react';
import { usePackageProduct } from '@jetshop/core/hooks/PackageProducts';
import { useIntl } from '@jetshop/intl';
import { useNotification } from '@jetshop/core/components/Notifications';
import { default as ButtonWithLoading } from '../../ui/Button';
import { PackageProductItem } from './PackageProductItem';
import packageProductQuery from './PackageProductQuery.gql';
import addMultipleToCartMutation from '../../Cart/queries/addMultipleToCart.gql';
import cartQuery from '../../Cart/queries/cartQuery.gql';
import { StyledButton } from '../../ui/StyledButton';
import { PushPrice } from '../../ui/StyledPrice';
import KlarnaPaymentInfo from '../KlarnaPayementInfo';
import { PackageToast } from './PackageToast';
import { Above } from '@jetshop/ui/Breakpoints';

const PackageProductWrapper = styled('div')`
  background-color: white;
  display: flex;
  flex-direction: column;

  margin-bottom: 24px;
  width: 100%;

  .error {
    margin-top: 1em;
    background: #faf3f3;
    padding: 1em;
    label {
      color: #ca0c0c;
    }
  }
  .error button {
    margin-top: 1em;
  }
  .package-product-items-wrapper {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
  }

  .push-style {
    &.price,
    &.new-price {
      font-size: 30px;
      line-height: 35px;
    }

    &.new-price {
      margin-left: 0;
    }

    &.old-price {
      font-size: 12px;
    }
  }
  .red-tilt-wrapper {
    .percentage {
      font-size: 11px;
    }
  }

  .RED {
    width: 100%;
    height: 45px;
  }
`;

function PackageProduct({ product, hideTarget }) {
  const ref = useRef(null);

  const {
    addPackageToCart,
    status,
    price,
    error,
    retry,
    updatePackageItem,
    packageProduct
  } = usePackageProduct({
    product,
    packageProductQuery
  });

  // validation is not enabled until the 'add to cart' button is pressed once
  const [validationEnabled, enableValidation] = React.useReducer(
    () => true,
    false
  );

  const t = useIntl();

  const buyable = status === 'buyable';

  function add() {
    addPackageToCart({
      addMultipleToCartMutation,
      cartQuery
    });
    hideTarget();
  }

  function scrollToProducts() {
    window.scrollTo({ top: ref.current.offsetTop, behavior: 'smooth' });
  }

  const [notify] = useNotification();

  useEffect(() => {
    if (status === 'added') {
      notify(<PackageToast product={product} />);
    }
  }, [notify, product, product.name, status]);

  if (status === 'loading') {
    return <LoadingState />;
  }

  return (
    <>
      <PackageProductWrapper ref={ref}>
        <Above breakpoint="lg">
          {matches => matches && <PushPrice {...price} />}
        </Above>

        <div className="package-product-items-wrapper">
          {packageProduct.package.items.map((item, idx) => {
            return (
              <PackageProductItem
                item={item}
                key={item.product.id}
                updatePackageItem={updatePackageItem}
                validationEnabled={validationEnabled}
                price={price.items[idx]}
                fetchingPrice={status === 'fetching_price'}
              />
            );
          })}
        </div>

        <StyledButton
          testid="package-product-add-to-cart"
          className={'RED'}
          clickCallback={
            buyable
              ? add
              : () => {
                  enableValidation();
                  scrollToProducts();
                }
          }
        >
          {status === 'added'
            ? t('Successfully added!')
            : status === 'invalid' && validationEnabled
            ? t('Select the variants')
            : t('Add to cart')}
        </StyledButton>
        <div className="klarna-wrapper">
          <KlarnaPaymentInfo product={product} />
        </div>
        {status === 'error' && <Error error={error} retry={retry} />}
      </PackageProductWrapper>
    </>
  );
}

function LoadingState() {
  const t = useIntl();
  return (
    <PackageProductWrapper>
      {t('Loading package details...')}
    </PackageProductWrapper>
  );
}

function Error({ error, retry }) {
  const t = useIntl();

  // TODO: API will be changed to allow a `abortOnError` or similar variable
  // for addMultipleToCart. Currently it's possible to end up in a state where
  // some items in the package failed and some succeeded. After the change,
  // complete failure will be the only case we need to handle.
  return (
    <div className="error">
      <label>
        {t('Something went wrong when { placeholder }', {
          placeholder:
            error.fromState === 'fetchingPrice'
              ? ' fetching price. This item cannot be added to the cart at this time'
              : ' adding to cart'
        })}
        .
      </label>
      {error.messages.map(err => (
        <div>{err}</div>
      ))}
      <ButtonWithLoading onClick={retry}>{t('Retry')}</ButtonWithLoading>
    </div>
  );
}

export default PackageProduct;
